// src/components/method/iconDraw.js
//
// Vanilla JS/CSS "drawn by a sparkle" animation for the /metodo page's
// family icons. No animation library. Given an <svg> whose direct shape
// children (path/circle) are already in the DOM, this loops forever,
// slowly:
//   1. OUTLINE  — draws each shape's stroke via the classic
//      stroke-dasharray/dashoffset technique, staggered shape-to-shape,
//      with a small coral sparkle riding the live drawing tip
//      (getPointAtLength sampled every animation frame), as the curves
//      form and close one after another.
//   2. FILL     — once every shape's outline is closed, the color drops
//      in as a "droplet": a clip-path circle appears small at the icon's
//      center, then grows past full coverage and bounces back to rest
//      (an explicit overshoot keyframe, not a plain ease), with a sparkle
//      explosion in the same fill color at the trigger moment.
//   3. HOLD, then reset back to blank and restart from 1 — an idle beat
//      at the fully-drawn state before the next lap begins.
//
// Mirrors the vanilla-JS conventions already established in
// src/components/home/cardMagicReveal.js (forceReflow pattern, prefers-
// reduced-motion guard, self-removing sparkle DOM nodes).

const STYLE_TAG_ID = 'icon-draw-styles';

const STROKE_WIDTH = 5;
const MIN_STEP_MS = 260;
const MAX_STEP_MS = 820;
const STEP_STAGGER_RATIO = 0.48; // next shape starts at 48% of the previous one's duration
const FILL_BOUNCE_MS = 900;
const HOLD_MS = 1500; // pause at the fully-drawn, fully-filled state
const RESET_GAP_MS = 300; // blank breath before the next lap's outline starts

function ensureStylesInjected() {
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_TAG_ID;
  style.textContent = `
    .icon-draw-shape {
      transition: fill 500ms ease, stroke 400ms ease, stroke-opacity 400ms ease;
    }
    .icon-draw-group {
      clip-path: circle(0% at 50% 50%);
    }
    .icon-draw-group.icon-draw-fill-kf {
      animation: icon-draw-fill-kf ${FILL_BOUNCE_MS}ms cubic-bezier(0.3, 0, 0.2, 1) forwards;
    }
    @keyframes icon-draw-fill-kf {
      0%   { clip-path: circle(0% at 50% 50%); }
      14%  { clip-path: circle(9% at 50% 50%); }
      55%  { clip-path: circle(175% at 50% 50%); }
      78%  { clip-path: circle(128% at 50% 50%); }
      100% { clip-path: circle(150% at 50% 50%); }
    }
    .icon-draw-tip {
      pointer-events: none;
    }
    .icon-draw-burst {
      position: absolute; left: 50%; top: 50%; border-radius: 50%;
      pointer-events: none; opacity: 0;
      animation: icon-draw-burst-kf 700ms ease-out forwards;
    }
    @keyframes icon-draw-burst-kf {
      0% { opacity: 0; transform: translate(-50%, -50%) scale(0.3); }
      30% { opacity: 1; }
      100% { opacity: 0; transform: var(--burst-end, translate(-50%, -50%)) scale(0.3); }
    }
    @media (prefers-reduced-motion: reduce) {
      .icon-draw-group, .icon-draw-group.icon-draw-fill-kf { animation: none; }
    }
  `;
  document.head.appendChild(style);
}

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function wait(ms) { return new Promise((resolve) => { window.setTimeout(resolve, ms); }); }
function forceReflow(el) { return el.offsetHeight; }

function spawnBurst(hostEl, color) {
  if (!hostEl) return;
  const count = 14;
  for (let k = 0; k < count; k++) {
    const angle = (k / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.6;
    const radius = 30 + Math.random() * 20;
    const dx = Math.cos(angle) * radius;
    const dy = Math.sin(angle) * radius;
    const size = 6 + Math.random() * 5;

    const el = document.createElement('i');
    el.className = 'icon-draw-burst';
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.background = color;
    el.style.setProperty('--burst-end', `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`);
    el.addEventListener('animationend', () => el.remove(), { once: true });
    window.setTimeout(() => el.remove(), 900);
    hostEl.appendChild(el);
  }
}

// Final fill color for a shape, by its role (see AnimatedIcon.jsx): 'ink'/
// 'paper' shapes keep the icon's original light-vs-dark relationship (using
// the site's own ink/paper tokens instead of the source SVG's arbitrary
// colors); 'accent' shapes take the per-icon brand color; 'line' shapes were
// pure outline in the source art and stay unfilled even after the reveal.
function roleFill(role, { color, inkColor, paperColor }) {
  if (role === 'ink') return inkColor;
  if (role === 'paper') return paperColor;
  if (role === 'line') return 'none';
  return color; // 'accent' (and default, if a shape carries no role)
}

// `shapeEls`: ordered array of <path>/<circle> elements (already in the DOM),
// each with a `data-role` attribute. `groupEl`: the <g> wrapping them, used
// for the fill-phase clip-path reveal. `tipEl`: a <circle> living inside the
// same <svg>, repositioned to trail the currently-drawing shape.
// `burstHostEl`: an absolutely-positioned HTML sibling of the <svg> sized to
// match it, used for the fill-phase sparkle burst (kept separate from the
// SVG's own coordinate space, same "two-layer" pattern as CardShowcase's
// overlay).
export function startIconDraw({ shapeEls, groupEl, tipEl, burstHostEl, color, inkColor, paperColor, startDelayMs = 0 }) {
  ensureStylesInjected();
  let stopped = false;
  const isStopped = () => stopped;
  const palette = { color, inkColor, paperColor };

  if (prefersReducedMotion()) {
    shapeEls.forEach((el) => {
      const role = el.dataset.role;
      el.style.fill = roleFill(role, palette);
      el.style.stroke = role === 'line' ? color : 'none';
    });
    if (groupEl) groupEl.style.clipPath = 'circle(150% at 50% 50%)';
    if (tipEl) tipEl.style.display = 'none';
    return () => { stopped = true; };
  }

  // Snap every shape back to "about to draw" — fill:none, full-length
  // dasharray/dashoffset (invisible outline, ready to animate to 0) — used
  // both before the first lap and to reset between loops.
  function primeOutlineState() {
    shapeEls.forEach((el) => {
      el.classList.add('icon-draw-shape');
      el.style.transition = 'none';
      el.style.fill = 'none';
      el.style.stroke = color;
      el.style.strokeWidth = String(STROKE_WIDTH);
      el.style.strokeOpacity = '1';
      let length = 0;
      try { length = el.getTotalLength(); } catch (_) { length = 200; }
      el.dataset.iconDrawLength = String(length);
      el.style.strokeDasharray = String(length);
      el.style.strokeDashoffset = String(length);
    });
    if (groupEl) {
      groupEl.classList.remove('icon-draw-fill-kf');
      groupEl.style.clipPath = 'circle(0% at 50% 50%)';
    }
    if (tipEl) {
      tipEl.style.color = color;
      tipEl.setAttribute('fill', color);
      tipEl.setAttribute('r', '6.5');
      tipEl.style.opacity = '0';
    }
    forceReflow(groupEl || shapeEls[0]);
  }

  primeOutlineState();

  async function outlinePhase() {
    for (let i = 0; i < shapeEls.length; i++) {
      if (stopped) return;
      const el = shapeEls[i];
      const length = Number(el.dataset.iconDrawLength) || 200;
      const durationMs = Math.min(MAX_STEP_MS, Math.max(MIN_STEP_MS, length * 1.7));

      el.style.transition = `stroke-dashoffset ${durationMs}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      el.style.strokeDashoffset = '0';

      // Sparkle tip rides this shape's live drawing point for its duration.
      if (tipEl) {
        tipEl.style.opacity = '1';
        const start = performance.now();
        const follow = (now) => {
          if (isStopped()) return;
          const t = Math.min(1, (now - start) / durationMs);
          let point;
          try { point = el.getPointAtLength(t * length); } catch (_) { point = null; }
          if (point) {
            tipEl.setAttribute('cx', String(point.x));
            tipEl.setAttribute('cy', String(point.y));
          }
          if (t < 1 && !isStopped()) window.requestAnimationFrame(follow);
        };
        window.requestAnimationFrame(follow);
      }

      const stepDelay = Math.round(durationMs * STEP_STAGGER_RATIO);
      // eslint-disable-next-line no-await-in-loop
      await wait(i === shapeEls.length - 1 ? durationMs : stepDelay);
    }
    if (stopped) return;
    if (tipEl) tipEl.style.opacity = '0';
  }

  function fillPhase() {
    shapeEls.forEach((el) => {
      const role = el.dataset.role;
      el.style.fill = roleFill(role, palette);
      el.style.stroke = color;
      el.style.strokeOpacity = role === 'line' ? '1' : '0';
    });
    if (groupEl) {
      forceReflow(groupEl);
      groupEl.classList.add('icon-draw-fill-kf');
    }
    spawnBurst(burstHostEl, color);
  }

  async function loop() {
    if (startDelayMs > 0) await wait(startDelayMs);
    while (!stopped) {
      // eslint-disable-next-line no-await-in-loop
      await outlinePhase();
      if (stopped) break;

      fillPhase();
      // eslint-disable-next-line no-await-in-loop
      await wait(FILL_BOUNCE_MS);
      if (stopped) break;

      // eslint-disable-next-line no-await-in-loop
      await wait(HOLD_MS);
      if (stopped) break;

      primeOutlineState();
      // eslint-disable-next-line no-await-in-loop
      await wait(RESET_GAP_MS);
    }
  }

  loop();

  return function stop() { stopped = true; };
}
